import { lazy, Suspense } from "react";
import { BrowserRouter } from "react-router-dom";
import { configure } from "mobx";
import { observer } from "mobx-react";
import { MantineProvider, v8CssVariablesResolver } from "@mantine/core";
import { Notifications } from "@mantine/notifications";
import { AudioProvider } from "./context/audio";
import { theme } from "./theme/mantine-theme";
import { colorSchemeManager } from "./theme/color-scheme";
import "@mantine/core/styles.css";
import "@mantine/notifications/styles.css";
import "./styles/global.css";
import { Header } from "./components/Header";
import { SpotlightSearch } from "./components/search/SpotlightSearch";
import { LoginNoticeModal } from "./components/LoginNoticeModal";
import { Content } from "./components/layout/Content";
import { SEO } from "./components/SEO";
import { AppRoutes } from "./components/routing/AppRoutes";
import { ScrollToTop } from "./components/routing/ScrollToTop";
import { ThemeMeta } from "./components/theme/ThemeMeta";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { Player } from "./components/player/Player";
import { useStores } from "./hooks/useStores";

const PlaylistModal = lazy(() =>
    import("./components/playlist/PlaylistModal").then(({ PlaylistModal }) => ({ default: PlaylistModal }))
);

const OptionalPlaylistModal = observer(() => {
    const { playlistStore } = useStores();

    if (!playlistStore.isOpen) {
        return null;
    }

    return (
        <Suspense fallback={null}>
            <PlaylistModal />
        </Suspense>
    );
});

configure({
    enforceActions: "never",
});

export const App = () => {
    return (
        <>
            <SEO />
            <MantineProvider
                colorSchemeManager={colorSchemeManager}
                cssVariablesResolver={v8CssVariablesResolver}
                defaultColorScheme="auto"
                theme={theme}
                withGlobalClasses>
                <ThemeMeta />
                <Notifications pauseResetOnHover="notification" />
                <AudioProvider>
                    <BrowserRouter>
                        <ErrorBoundary>
                            <>
                                <ScrollToTop />
                                <ErrorBoundary>
                                    <SpotlightSearch />
                                </ErrorBoundary>
                                <Content>
                                    <ErrorBoundary>
                                        <Header />
                                    </ErrorBoundary>
                                    <ErrorBoundary>
                                        <AppRoutes />
                                    </ErrorBoundary>
                                </Content>
                                <ErrorBoundary>
                                    <OptionalPlaylistModal />
                                </ErrorBoundary>
                                <ErrorBoundary>
                                    <LoginNoticeModal />
                                </ErrorBoundary>
                            </>
                        </ErrorBoundary>
                        <ErrorBoundary>
                            <Player />
                        </ErrorBoundary>
                    </BrowserRouter>
                </AudioProvider>
            </MantineProvider>
        </>
    );
};
